import { SiteLayout } from "@/components/layout/SiteLayout";
import { RelatedPages } from "@/components/RelatedPages";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NetworkRouter } from "@/components/NetworkRouter";
import { motion, type Variants } from "framer-motion";
import { withNetworkUtm } from "@/lib/network";
import {
  breadcrumbSchema,
  collectionPageSchema,
  faqSchema,
  mobileApplicationSchema,
  personSchema,
} from "@/lib/seo";

import { NetworkNextStep } from "@/components/NetworkNextStep";
const WORKSPACE_SRC = "/landing/projects-workspace.webp";
const WORKSPACE_ALT =
  "A minimalist modern clinical workspace with a warm walnut desk, closed laptop, soft beige notebook, and a small green plant in soft daylight.";

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

interface ProjectEntry {
  name: string;
  whatItIs: string;
  whyItExists?: string;
  role: string;
  href?: string;
  linkLabel?: string;
  note?: string;
}

interface ProjectCategory {
  eyebrow: string;
  entries: ProjectEntry[];
}

const CATEGORIES: ProjectCategory[] = [
  {
    eyebrow: "Clinical Care",
    entries: [
      {
        name: "shrinkMD",
        whatItIs: "An independent multistate telepsychiatry practice.",
        whyItExists:
          "Because the wait to see a psychiatrist in most of the country is still measured in months, not days.",
        role: "Founder and practicing psychiatrist.",
        href: withNetworkUtm("https://shrinkmd.com/", "projects_page"),
        linkLabel: "shrinkmd.com",
      },
    ],
  },
  {
    eyebrow: "Education",
    entries: [
      {
        name: "Shrinkopedia",
        whatItIs: "A clinically reviewed mental health encyclopedia.",
        whyItExists:
          "Because the explanations on the internet are either too thin or too academic, and almost none are written by clinicians.",
        role: "Medical editor and contributing author.",
        href: withNetworkUtm("https://shrinkopedia.com/", "projects_page"),
        linkLabel: "shrinkopedia.com",
      },
      {
        name: "AnxietyResource",
        whatItIs: "The practical anxiety education site in the network.",
        whyItExists:
          "Anxiety isn't one experience. It deserves its own site with practical, condition-specific guidance.",
        role: "Medical editor.",
        href: withNetworkUtm("https://anxietyresource.org/", "projects_page"),
        linkLabel: "anxietyresource.org",
      },
      {
        name: "DepressionResource",
        whatItIs: "The practical depression education site in the network.",
        whyItExists:
          "Depression has its own physiology, its own treatments, and its own course. It needs dedicated space.",
        role: "Medical editor.",
        href: withNetworkUtm("https://depressionresource.org/", "projects_page"),
        linkLabel: "depressionresource.org",
      },
      {
        name: "PsychiatryRx",
        whatItIs:
          "Plain-language medication guides for psychiatric and sleep medications.",
        whyItExists:
          "Because the official labeling reads like it was written for pharmacists. Patients need a translation.",
        role: "Medical editor and author.",
        href: withNetworkUtm("https://psychiatryrx.org/", "projects_page"),
        linkLabel: "psychiatryrx.org",
      },
      {
        name: "AnxietyResearch",
        whatItIs: "An editorial research and data layer for anxiety in the U.S.",
        whyItExists:
          "The research literature is rich and almost none of it is translated for general readers.",
        role: "Medical editor and named author on the evidence summaries and reports.",
        href: withNetworkUtm("https://anxietyresearch.org/", "projects_page"),
        linkLabel: "anxietyresearch.org",
      },
      {
        name: "ShrinkDaily",
        whatItIs:
          "The daily mental health education site in the network. One short, evidence-based concept at a time.",
        whyItExists:
          "Because understanding your mind works better as a daily habit than as a one-time read.",
        role: "Founder and medical editor.",
        href: withNetworkUtm("https://shrinkdaily.com/", "projects_page"),
        linkLabel: "shrinkdaily.com",
      },
    ],
  },
  {
    eyebrow: "Practical Tools",
    entries: [
      {
        name: "shrinQ",
        whatItIs:
          "A structured educational system for overthinking and the anxiety loops that don't yield to insight alone.",
        whyItExists: "Because seeing the pattern isn't the same as working with it.",
        role: "Founder and program author.",
        href: withNetworkUtm("https://shrinq.com/", "projects_page"),
        linkLabel: "shrinq.com",
      },
      {
        name: "Unstuck",
        whatItIs:
          "A wellness app for reflection, journaling, and emotional awareness.",
        whyItExists:
          "Because there's a moment between \u201CI'm overwhelmed\u201D and \u201CI need a clinician\u201D where a well-designed tool can help.",
        role: "Founder.",
        href: withNetworkUtm("https://beunstuck.app/", "projects_page"),
        linkLabel: "beunstuck.app",
      },
      {
        name: "QuitScrolling",
        whatItIs:
          "An attention tool built on one idea: spend more time living and less time scrolling.",
        whyItExists:
          "Because the attention pull is now a meaningful contributor to anxiety for a lot of modern adults.",
        role: "Founder.",
        href: withNetworkUtm("https://quitscrolling.app/", "projects_page"),
        linkLabel: "quitscrolling.app",
      },
    ],
  },
  {
    eyebrow: "Publishing",
    entries: [
      {
        name: "Your Mind Is Full of Sh*t",
        whatItIs:
          "A psychiatrist's guide to understanding overthinking, anxiety, and the lies in your head.",
        whyItExists:
          "Because the patterns that run people's lives haven't been named clearly enough in popular writing.",
        role: "Author.",
        href: "/books/your-mind-is-full-of-shit",
        linkLabel: "See the book",
      },
      {
        name: "Your Mind Is Full of Sh*t: The Workbook",
        whatItIs: "The companion workbook to the first book. The do-the-work piece.",
        role: "Author.",
        href: "/books",
        linkLabel: "See the books",
      },
      {
        name: "The Havoc in Your Head",
        whatItIs:
          "A psychiatrist's reset for anxiety, overthinking, panic, and the thoughts that won't let go.",
        role: "Author.",
        href: "/books",
        linkLabel: "See the books",
      },
    ],
  },
  {
    eyebrow: "Profession and Thought Leadership",
    entries: [
      {
        name: "shrinkiatry",
        whatItIs:
          "The site for the inside view of psychiatry as a profession, by someone who actually practices.",
        whyItExists:
          "Because most coverage of psychiatry is either dismissive or oversold.",
        role: "Founder and author.",
        href: withNetworkUtm("https://shrinkiatry.com/", "projects_page"),
        linkLabel: "shrinkiatry.com",
      },
      {
        name: "shariqrefai.com (this site)",
        whatItIs: "The founder, author, reviewer, and media site.",
        whyItExists:
          "Because the writing, the books, the speaking, and the founder perspective deserve a dedicated home.",
        role: "Author and founder.",
        note: "You're here.",
      },
    ],
  },
  {
    eyebrow: "Network",
    entries: [
      {
        name: "The Shrink Network",
        whatItIs:
          "The umbrella that connects all of the above and explains how the pieces fit together.",
        role: "Founder.",
        href: withNetworkUtm("https://shrinknetwork.com/", "projects_page"),
        linkLabel: "shrinknetwork.com",
      },
    ],
  },
];

function EntryCard({ entry }: { entry: ProjectEntry }) {
  const isInternal = entry.href?.startsWith("/");
  return (
    <div className="border border-border bg-background p-6 md:p-8 flex flex-col gap-3">
      <h3 className="text-2xl font-serif text-primary leading-tight">
        {entry.name}
      </h3>
      <p className="text-secondary font-light leading-relaxed">
        <strong className="text-primary font-medium">What it is:</strong>{" "}
        {entry.whatItIs}
      </p>
      {entry.whyItExists ? (
        <p className="text-secondary font-light leading-relaxed">
          <strong className="text-primary font-medium">Why it exists:</strong>{" "}
          {entry.whyItExists}
        </p>
      ) : null}
      <p className="text-secondary font-light leading-relaxed">
        <strong className="text-primary font-medium">My role:</strong>{" "}
        {entry.role}
      </p>
      {entry.href ? (
        <div className="pt-1">
          {isInternal ? (
            <Link
              href={entry.href}
              className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:text-primary transition-colors"
            >
              {entry.linkLabel} <span aria-hidden="true">&rarr;</span>
            </Link>
          ) : (
            <a
              href={entry.href}
              rel="noopener"
              className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:text-primary transition-colors"
            >
              Visit {entry.linkLabel} <span aria-hidden="true">&rarr;</span>
            </a>
          )}
        </div>
      ) : entry.note ? (
        <p className="pt-1 text-sm font-medium text-muted-foreground italic">
          {entry.note}
        </p>
      ) : null}
    </div>
  );
}

export default function Projects() {
  const SEO_TITLE = "Projects Founded or Built by Shariq Refai, MD, MBA";
  const SEO_DESCRIPTION =
    "The projects, ventures, and publications I founded, built, or medically review across The Shrink Network, from clinical care to apps, books, and references.";

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Projects", path: "/projects" },
  ];

  const collection = collectionPageSchema({
    path: "/projects",
    title: SEO_TITLE,
    description: SEO_DESCRIPTION,
    breadcrumbs,
    items: [
      { name: "shrinkMD", path: "https://shrinkmd.com" },
      { name: "Shrinkopedia", path: "https://shrinkopedia.com" },
      { name: "AnxietyResource.org", path: "https://anxietyresource.org" },
      { name: "DepressionResource.org", path: "https://depressionresource.org" },
      { name: "PsychiatryRx.org", path: "https://psychiatryrx.org" },
      { name: "AnxietyResearch.org", path: "https://anxietyresearch.org" },
      { name: "ShrinkDaily", path: "https://shrinkdaily.com" },
      { name: "shrinQ", path: "https://shrinq.com" },
      { name: "Unstuck", path: "https://beunstuck.app" },
      { name: "QuitScrolling", path: "https://quitscrolling.app" },
      { name: "shrinkMD Publishing", path: "/books" },
      { name: "shrinkiatry", path: "https://shrinkiatry.com" },
      { name: "The Shrink Network", path: "https://shrinknetwork.com" },
    ],
  });

  return (
    <SiteLayout>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        url="/projects"
        image="/og-projects.jpg"
        keywords="shrinkMD, shrinQ, Unstuck app, QuitScrolling, Shariq Refai projects, telepsychiatry, Shrinkopedia, AnxietyResource, DepressionResource, AnxietyResearch, PsychiatryRx, shrinkiatry, The Shrink Network"
        schema={[
          personSchema,
          collection,
          mobileApplicationSchema,
          breadcrumbSchema(breadcrumbs),
          faqSchema([
            {
              question: "What is shrinkMD?",
              answer:
                "shrinkMD is an independent multistate telepsychiatry company founded by Dr. Shariq Refai. shrinkMD is a separate clinical entity from shariqrefai.com, which is an educational author and media platform. Clinical care inquiries should be directed to shrinkmd.com.",
            },
            {
              question: "What is shrinQ?",
              answer:
                "shrinQ (shrinq.com) is a structured, self-guided educational system for overthinking and anxiety loops, created by Dr. Refai. It's the educational arm of his work, separate from his clinical practice shrinkMD. It isn't therapy, diagnosis, treatment, or a substitute for care from a licensed clinician.",
            },
            {
              question:
                "What are AnxietyResource.org, DepressionResource.org, AnxietyResearch.org, and PsychiatryRx.org?",
              answer:
                "All four are independent editorial publications in The Shrink Network. AnxietyResource.org covers anxiety; DepressionResource.org covers depression; AnxietyResearch.org translates current anxiety research into plain-language summaries; PsychiatryRx.org publishes plain-language, psychiatrist-reviewed guides to psychiatric and sleep medications. Dr. Refai serves as the medical editor for each. None is a clinical service, pharmacy, or place to obtain prescriptions.",
            },
          ]),
        ]}
      />

      {/* Intro */}
      <section className="intro pt-24 pb-12 md:pt-32 md:pb-16 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs
            items={[{ name: "Home", href: "/" }, { name: "Projects" }]}
          />
          <motion.div
            className="grid md:grid-cols-12 gap-10 md:gap-14 items-center"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            <div className="md:col-span-7 order-2 md:order-1 flex flex-col gap-5">
              <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
                Founder Projects
              </span>
              <h1 className="text-4xl md:text-5xl font-serif text-primary leading-tight tracking-tight">
                Projects founded, built, and reviewed
              </h1>
              <p className="text-lg md:text-xl text-secondary font-light leading-relaxed">
                The projects, ventures, and publications I founded, built, or
                medically review.
              </p>
              <p className="text-base text-secondary font-light leading-relaxed">
                These are the projects I've started, am building, or actively
                medically review. They're separated by what they do, not by what
                they're called. Clinical care lives in one place. Education lives
                in its own places. Practical tools have their own homes.
                Publishing is its own category. The founder site you're reading
                sits at the perspective layer. Each item below names my role, what
                the project does, and where it fits in the network.
              </p>
            </div>
            <div className="md:col-span-5 order-1 md:order-2">
              <figure className="relative m-0">
                <img
                  src={WORKSPACE_SRC}
                  alt={WORKSPACE_ALT}
                  width={1200}
                  height={900}
                  className="w-full h-auto object-cover aspect-[4/3] shadow-sm"
                  loading="eager"
                  fetchPriority="high"
                />
              </figure>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      {CATEGORIES.map((category, i) => (
        <section
          key={category.eyebrow}
          className={`project-category py-16 md:py-20 px-4 md:px-8 ${
            i % 2 === 0 ? "bg-background" : "bg-muted/30 border-y border-border/50"
          }`}
        >
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-accent text-xs font-bold tracking-[0.25em] uppercase m-0">
              {category.eyebrow}
            </h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {category.entries.map((entry) => (
                <EntryCard key={entry.name} entry={entry} />
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Continue */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-secondary text-white">
        <div className="container mx-auto max-w-5xl">
          <NetworkRouter
            variant="dark"
            eyebrow="Continue"
            cards={[
              { label: "Read why I built it all", href: "/founder-of-the-shrink-network" },
              { label: "Read the medical reviewer profile", href: "/medical-reviewer" },
              { label: "Read what I'm building next", href: "/what-im-building" },
              { label: "Read the books", href: "/books" },
            ]}
          />
        </div>
      </section>
    
      <section className="bg-background py-20 border-t border-border">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <NetworkNextStep
            campaign="projects_next_step"
            destinations={["shrinkmd", "shrinq", "shrinkopedia"]}
          />
        </div>
      </section>
      <RelatedPages
        entries={[
          { href: "/about", label: "About Shariq Refai, MD, MBA", why: "The training, credentials, and background behind the projects." },
          { href: "/books", label: "Books by Shariq Refai", why: "The books published by shrinkMD Publishing, the imprint I run." },
          { href: "/founder-of-the-shrink-network", label: "Founder of The Shrink Network", why: "Why the projects are structured as a coordinated ecosystem." },
          { href: "/disclosures", label: "Relationships & financial disclosure", why: "Ownership interests and financial relationships behind each project." },
        ]}
      />
    </SiteLayout>
  );
}
